import React, { MouseEventHandler, useEffect, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Connection, Destination, Organization } from "../common/ObjectTypes";
import DestinationDetailsModalTable from "./DestinationDetailsModalTable";
import DestinationDetailsModalConfig from "./DestinationDetailsModalConfig";
import Spinner from "../common/Spinner";
import { connectionDetailsClasses } from "../common/tailwindClassnames";

type DestinationDetailsProps = {
    currentMemberIsAdmin: boolean,
    destination: Destination,
    destinationTitle: string,
    destSubPage: string | undefined,
    onSelectSubPage: Function,
    connections: Connection[],
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    onToggleModal: MouseEventHandler,
    requestSort: Function,
    loadingConnections: boolean,
    onChangeSelectedDestination: Function,
    onChangeSelectedDestinationConfig: Function,
    onTestAction: Function,
    testActionLoading: boolean,
    testActionResponse: any,
    testActionStatus: number,
    onChangeSelectedDestinationOwner: Function,
    onSave: (e: React.FormEvent<Element>, action?: "save" | "saveAndClose") => void,
    errors: any,
    rsjfErrors: any,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number,
    scrollableContentRef?: RefObject<HTMLDivElement>
}

const DestinationDetailsModal = ({
    currentMemberIsAdmin,
    destination,
    destinationTitle,
    destSubPage,
    onSelectSubPage,
    connections,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    onToggleModal,
    requestSort,
    loadingConnections,
    onChangeSelectedDestination,
    onChangeSelectedDestinationConfig,
    onTestAction,
    testActionLoading,
    testActionResponse,
    testActionStatus,
    onChangeSelectedDestinationOwner,
    onSave,
    errors,
    rsjfErrors,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset,
    scrollableContentRef
}: DestinationDetailsProps) => {
    const { t } = useTranslation();

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, []);

    // Only allow 'details' or 'configuration' as valid subpages
    const allowedSubPages = ['details', 'configuration'];
    const subPage = allowedSubPages.includes(destSubPage || '') ? destSubPage : 'details';

    return (
        <div
            key={subPage}
            className="z-20 flex flex-col w-full h-screen bg-white dark:bg-neutral-gray-900 transition-colors duration-300"
        >
            {/* top portion */}
            <div className={connectionDetailsClasses.header}>
                <div className="flex flex-row items-center pb-2 w-full bg-white dark:bg-neutral-gray-900 transition-colors duration-300">
                    <button
                        type="button"
                        onClick={onToggleModal}
                        className="mr-2 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-2 flex items-center dark:hover:bg-gray-800 dark:hover:text-white transition-colors duration-300"
                        aria-label="Back"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"></path></svg>
                    </button>
                    <h1 className="text-2xl sm:text-3xl font-semibold text-off-black dark:text-white transition-colors duration-300">{destinationTitle}</h1>
                </div>
                <div className="flex flex-col my-1 px-4 w-full bg-white dark:bg-neutral-gray-900 transition-colors duration-300">
                    {/* menu tabs */}
                    <div className={pageLoaded ? "w-full flex flex-row font-semibold text-medium-gray dark:text-neutral-gray-400 visible transition-colors duration-300" : "invisible"}>
                        <label className={(destSubPage === "details") ? "border-2 border-t-white border-l-white border-r-white dark:border-t-neutral-gray-900 dark:border-l-neutral-gray-900 dark:border-r-neutral-gray-900 border-b-status-green text-dark-gray dark:text-white mr-3 py-3 cursor-pointer transition-colors duration-300" : "border-2 border-white dark:border-neutral-gray-900 mr-3 py-3 cursor-pointer transition-colors duration-300"}
                            onClick={() => onSelectSubPage("details")}>
                            {t("Connections")}
                        </label>
                        <label className={(destSubPage === "configuration") ? "border-2 border-t-white border-l-white border-r-white dark:border-t-neutral-gray-900 dark:border-l-neutral-gray-900 dark:border-r-neutral-gray-900 border-b-status-green text-dark-gray dark:text-white mr-3 py-3 cursor-pointer transition-colors duration-300" : "border-2 border-white dark:border-neutral-gray-900 mr-3 py-3 cursor-pointer transition-colors duration-300"}
                            onClick={() => onSelectSubPage("configuration")}>
                            {t("Configuration")}
                        </label>
                    </div>
                </div>
            </div>

            {/* scrollable content */}
            <div
                key={destSubPage}
                className="bg-very-light-gray dark:bg-off-black flex flex-col overflow-y-auto flex-grow w-full  min-w-[400px] mt-1 scrollbar-thin scrollbar-thumb-neutral-gray-400 dark:scrollbar-thumb-neutral-gray-700 scrollbar-track-transparent hover:scrollbar-thumb-neutral-gray-500 dark:hover:scrollbar-thumb-neutral-gray-600"
                ref={scrollableContentRef}
            >
                {!pageLoaded ?
                    <div className="flex justify-center items-center h-full dark:bg-dark-gray transition-colors duration-300">
                        <Spinner />
                    </div>
                    :
                    <div className="px-4 py-3 h-full transition-colors duration-300"> {/* Make content take full height */}
                        {(destSubPage === "details") && <DestinationDetailsModalTable
                            connections={connections}
                            requestSort={requestSort}
                            loadingConnections={loadingConnections}
                            onPageClicked={onPageClicked}
                            totalCount={totalCount}
                            pageLoaded={pageLoaded}
                            take={take}
                            offset={offset}
                        />}
                        {(destSubPage === "configuration") && <DestinationDetailsModalConfig
                            currentMemberIsAdmin={currentMemberIsAdmin}
                            destination={destination}
                            organizations={organizations}
                            loadMoreOrgs={loadMoreOrgs}
                            orgInputChange={orgInputChange}
                            onChange={onChangeSelectedDestination}
                            onChangeSelectedDestinationOwner={onChangeSelectedDestinationOwner}
                            onChangeSelectedDestinationConfig={onChangeSelectedDestinationConfig}
                            onTestAction={onTestAction}
                            testActionLoading={testActionLoading}
                            testActionResponse={testActionResponse}
                            testActionStatus={testActionStatus}
                            onSave={onSave}
                            onToggleModal={onToggleModal}
                            errors={errors}
                            rsjfErrors={rsjfErrors}
                        />}
                    </div>}
            </div>
        </div>
    );
};

export default DestinationDetailsModal;